/**
 * El conteo en vivo de un público (SPEC-BD §6.4): cuántos entran y cuántos pueden recibir hoy.
 *
 * Lo calcula el backend con el mismo cálculo que el chequeo de una campaña (como una promoción, el peor
 * caso): «pueden recibir» ya descuenta no contactar, sin consentimiento, fatiga, teléfonos dudosos... Los
 * teléfonos de los ejemplos vienen enmascarados y se vuelven a enmascarar acá.
 */

import { Loader2 } from "lucide-react";
import type { ConteoPublico } from "../../types.base";
import { ORDEN_GATES } from "../../types";
import { MOTIVO_EXCLUSION } from "../../logic/textos";
import { destinoParaMostrar } from "../../logic/formato";
import { num } from "../../../campana-nueva/utils/format";

interface Props {
  conteo: ConteoPublico | null;
  contando: boolean;
  error: string;
  /** El filtro todavía no se puede contar (una condición a medio armar): se dice en vez de mostrar un número viejo como vigente. */
  filtroIncompleto: boolean;
  onReintentar: () => void;
}

export function PanelConteo({ conteo, contando, error, filtroIncompleto, onReintentar }: Props) {
  const gris = contando || filtroIncompleto;
  const excluidos = conteo ? ORDEN_GATES.filter((m) => (conteo.excluidos?.[m] ?? 0) > 0) : [];

  return (
    <aside className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm space-y-4" aria-live="polite">
      {error ? (
        <p className="text-sm text-red-700" role="alert">
          No pudimos contar. <button type="button" onClick={onReintentar} className="font-semibold underline">Reintentar</button>
          <span className="block text-xs mt-1">{error}</span>
        </p>
      ) : !conteo && contando ? (
        <div className="space-y-2" aria-busy="true"><div className="h-10 w-32 rounded-xl bg-slate-100 animate-pulse" /><div className="h-4 w-40 rounded bg-slate-100 animate-pulse" /></div>
      ) : conteo ? (
        <div>
          <p className={`text-[34px] leading-none font-semibold tracking-tight ${gris ? "text-slate-300" : "text-slate-900"}`}>{num(conteo.total)}</p>
          <p className="text-sm text-slate-600 mt-1">{conteo.total === 1 ? "contacto entra" : "contactos entran"}</p>
          <p className={`text-sm mt-2 ${gris ? "text-slate-400" : "text-emerald-700"}`}>
            <strong>{num(conteo.puedenRecibir)}</strong> pueden recibir hoy
          </p>
        </div>
      ) : null}

      {contando && <p className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Contando…</p>}
      {filtroIncompleto && !contando && <p className="text-xs text-slate-500">Completá las condiciones para contar.</p>}

      {conteo && !error && (
        <>
          {excluidos.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Quedan afuera</h4>
              <ul className="space-y-1 text-sm">
                {excluidos.map((m) => (
                  <li key={m} className="flex items-center justify-between gap-3">
                    <span className="text-slate-600">{MOTIVO_EXCLUSION[m]?.titulo ?? m}</span>
                    <span className="tabular-nums text-slate-900 font-semibold">{num(conteo.excluidos[m] ?? 0)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {conteo.ejemplos.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Por ejemplo</h4>
              <ul className="space-y-1 text-sm">
                {conteo.ejemplos.slice(0, 5).map((e, i) => (
                  <li key={i} className="flex items-center justify-between gap-3">
                    <span className="truncate text-slate-700">{e.nombre || "Sin nombre"}</span>
                    <span className="font-mono text-xs text-slate-500 whitespace-nowrap">{destinoParaMostrar(e.destino, false)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {conteo.supuestos && <p className="text-[11px] text-slate-400 leading-relaxed">{conteo.supuestos}</p>}
        </>
      )}
      <p className="text-[11px] text-slate-400 leading-relaxed">Cuentan los mensajes de WhatsApp a ese número por las dos vías: WhatsApp oficial (API) y las líneas por QR.</p>
    </aside>
  );
}

export default PanelConteo;
